(() => {
  const selector = '[data-cv-s4-6cam-controller]';
  const controllers = new WeakMap();

  class CVS46CAMPurchase {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.variants = this.readJson('[data-cv-s4-6cam-variants]');
      this.gallery = this.readJson('[data-cv-s4-6cam-gallery]');
      this.optionGroups = [...root.querySelectorAll('[data-cv-s4-6cam-option]')];
      this.form = root.querySelector('[data-cv-s4-6cam-product-form]');
      this.variantInput = root.querySelector('[data-cv-s4-6cam-variant-input]');
      this.quantityInput = root.querySelector('[data-cv-s4-6cam-quantity]');
      this.heroImage = root.querySelector('[data-cv-s4-6cam-hero-image]');
      this.mobileSource = root.querySelector('[data-cv-s4-6cam-mobile-source]');
      this.addButton = root.querySelector('[data-cv-s4-6cam-add]');
      this.paymentTerms = root.querySelector('shopify-payment-terms');
      this.dialog = root.querySelector('[data-cv-s4-6cam-dialog]');
      this.dialogImage = root.querySelector('[data-cv-s4-6cam-gallery-image]');
      this.dialogCaption = root.querySelector('[data-cv-s4-6cam-gallery-caption]');
      this.liveRegion = root.querySelector('[data-cv-s4-6cam-live]');
      this.returnFocus = null;
      this.galleryIndex = 0;

      if (!this.form || !this.variantInput || !this.variants.length) return;
      this.bind();
      this.bindVeroCollisionAvoidance();
      this.bindLoadingState();
      this.restoreFromUrl(false);
    }

    readJson(query) {
      const node = this.root.querySelector(query);
      if (!node) return [];
      try {
        return JSON.parse(node.textContent || '[]');
      } catch (_) {
        return [];
      }
    }

    bind() {
      const signal = this.abort.signal;

      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-cv-s4-6cam-option] input')) {
          this.update(this.findByOptions(), { historyMode: 'push', announce: true });
        }
        if (event.target.matches('[data-cv-s4-6cam-quantity]')) this.normalizeQuantity();
      }, { signal });

      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-cv-s4-6cam-quantity-minus]')) {
          event.preventDefault();
          this.stepQuantity(-1);
          return;
        }
        if (event.target.closest('[data-cv-s4-6cam-quantity-plus]')) {
          event.preventDefault();
          this.stepQuantity(1);
          return;
        }
        const galleryTrigger = event.target.closest('[data-cv-s4-6cam-gallery-open]');
        if (galleryTrigger) {
          event.preventDefault();
          this.openGallery(galleryTrigger);
          return;
        }
        if (event.target.closest('[data-cv-s4-6cam-gallery-close]')) this.closeGallery();
        if (event.target.closest('[data-cv-s4-6cam-gallery-previous]')) this.showGallery(this.galleryIndex - 1);
        if (event.target.closest('[data-cv-s4-6cam-gallery-next]')) this.showGallery(this.galleryIndex + 1);
      }, { signal });

      window.addEventListener('popstate', () => this.restoreFromUrl(false), { signal });

      if (this.dialog) {
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeGallery();
        }, { signal });

        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.closeGallery();
        }, { signal });

        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeGallery();
            return;
          }

          if (event.key === 'ArrowLeft') this.showGallery(this.galleryIndex - 1);
          if (event.key === 'ArrowRight') this.showGallery(this.galleryIndex + 1);
          if (event.key === 'Tab') this.trapFocus(event);
        }, { signal });

        this.dialog.addEventListener('close', () => {
          this.dialogImage?.removeAttribute('src');
          document.documentElement.classList.remove('cv-s4-6cam-dialog-open');
          this.returnFocus?.focus();
          this.returnFocus = null;
        }, { signal });
      }
    }


    bindVeroCollisionAvoidance() {
      const signal = this.abort.signal;
      const media = window.matchMedia('(min-width: 0px)');

      this.scheduleVeroPosition = () => {
        cancelAnimationFrame(this.veroFrame);
        this.veroFrame = requestAnimationFrame(() => this.positionVeroLauncher(media));
      };

      const attach = () => {
        const chat = document.querySelector('clearvero-chat[data-position="right"]');
        if (!chat) return false;
        if (this.veroChat === chat) {
          this.scheduleVeroPosition();
          return true;
        }

        this.veroObserver?.disconnect();
        this.veroChat = chat;
        this.veroObserver = new MutationObserver(this.scheduleVeroPosition);
        this.veroObserver.observe(chat, {
          attributes: true,
          attributeFilter: ['data-open'],
          childList: true,
          subtree: true,
        });
        chat.addEventListener('click', this.scheduleVeroPosition, { signal });
        this.scheduleVeroPosition();
        return true;
      };

      if (!attach()) {
        this.veroDocumentObserver = new MutationObserver(() => {
          if (!attach()) return;
          this.veroDocumentObserver?.disconnect();
        });
        this.veroDocumentObserver.observe(document.documentElement, { childList: true, subtree: true });
      }

      const scrollRoot = document.querySelector('.page-wrapper');
      scrollRoot?.addEventListener('scroll', this.scheduleVeroPosition, { passive: true, signal });
      window.addEventListener('scroll', this.scheduleVeroPosition, { passive: true, signal });
      window.addEventListener('resize', this.scheduleVeroPosition, { passive: true, signal });
      media.addEventListener('change', this.scheduleVeroPosition, { signal });
    }

    positionVeroLauncher(media) {
      const chat = this.veroChat;
      if (!chat) return;

      if (!media.matches) {
        chat.style.removeProperty('--cv-s4-6cam-vero-bottom');
        chat.style.removeProperty('--cv-s4-6cam-vero-right');
        chat.removeAttribute('data-cv-s4-6cam-docked');
        return;
      }

      if (chat.dataset.open === 'true') {
        const panelOffset = document.documentElement.clientWidth <= 600 ? 14 : 20;
        chat.style.setProperty('--cv-s4-6cam-vero-bottom', String(panelOffset) + 'px');
        chat.style.setProperty('--cv-s4-6cam-vero-right', String(panelOffset) + 'px');
        chat.setAttribute('data-cv-s4-6cam-docked', 'panel-open');
        return;
      }

      const launcher = chat.querySelector('.cv-launcher');
      if (!launcher || !launcher.getClientRects().length) return;

      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight;
      const launcherRect = launcher.getBoundingClientRect();
      const launcherHeight = launcherRect.height;
      const launcherWidth = launcherRect.width;
      const launcherFocusPadding = 3;
      const controlFocusPadding = 6;
      const minimumTop = 12;
      const slotStep = launcherHeight + 16;
      const narrowViewport = viewportWidth <= 600;
      const compactViewport = viewportWidth >= 821 && viewportWidth <= 1024;
      const baseBottom = narrowViewport ? 14 : 20;
      const baseRight = compactViewport ? 0 : (narrowViewport ? 14 : 20);
      const selectors = [
        'a[href]',
        'button',
        'input:not([type="hidden"])',
        'select',
        'textarea',
        'summary',
        '[role="button"]',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');

      const blockers = [...document.querySelectorAll(selectors)]
        .filter((node) => !chat.contains(node) && !node.closest('[hidden], dialog:not([open])'))
        .map((node) => node.getBoundingClientRect())
        .filter((rect) => (
          rect.width > 0
          && rect.height > 0
          && rect.bottom > 0
          && rect.top < viewportHeight
          && rect.right > 0
          && rect.left < viewportWidth
        ));

      let selectedBottom = baseBottom;
      let selectedLane = {
        name: 'right',
        left: viewportWidth - baseRight - launcherWidth,
        right: viewportWidth - baseRight,
        rightOffset: baseRight,
      };
      let resolved = false;
      const lanes = [
        selectedLane,
        {
          name: 'left',
          left: baseRight,
          right: baseRight + launcherWidth,
          rightOffset: viewportWidth - baseRight - launcherWidth,
        },
      ];

      for (const lane of lanes) {
        for (
          let candidateBottom = baseBottom;
          viewportHeight - candidateBottom - launcherHeight >= minimumTop;
          candidateBottom += slotStep
        ) {
          const candidate = {
            left: lane.left - launcherFocusPadding,
            right: lane.right + launcherFocusPadding,
            top: viewportHeight - candidateBottom - launcherHeight - launcherFocusPadding,
            bottom: viewportHeight - candidateBottom + launcherFocusPadding,
          };
          const collides = blockers.some((rect) => (
            candidate.left < rect.right + controlFocusPadding
            && candidate.right > rect.left - controlFocusPadding
            && candidate.top < rect.bottom + controlFocusPadding
            && candidate.bottom > rect.top - controlFocusPadding
          ));

          if (!collides) {
            selectedBottom = candidateBottom;
            selectedLane = lane;
            resolved = true;
            break;
          }
        }
        if (resolved) break;
      }

      chat.style.setProperty('--cv-s4-6cam-vero-bottom', String(selectedBottom) + 'px');
      chat.style.setProperty('--cv-s4-6cam-vero-right', String(selectedLane.rightOffset) + 'px');
      chat.setAttribute('data-cv-s4-6cam-docked', resolved ? selectedLane.name + '-clear' : 'suspended');
    }

    bindLoadingState() {
      if (!this.addButton) return;
      const sync = () => {
        this.addButton = this.root.querySelector('[data-cv-s4-6cam-add]');
        if (!this.addButton) return;

        const busy = this.addButton.hasAttribute('data-added');
        this.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
        this.addButton.disabled = !this.currentVariant?.available;

        clearTimeout(this.loadingFallback);
        if (busy) this.loadingFallback = setTimeout(() => this.settleLoadingState(), 3000);
      };

      this.loadingObserver = new MutationObserver(sync);
      this.loadingObserver.observe(this.root, {
        attributes: true,
        attributeFilter: ['data-added'],
        childList: true,
        subtree: true,
      });

      const errorRegion = this.root.querySelector('[ref="addToCartTextError"]');
      if (errorRegion) {
        this.errorObserver = new MutationObserver(() => {
          if (errorRegion.textContent.trim()) this.settleLoadingState();
        });
        this.errorObserver.observe(errorRegion, { childList: true, characterData: true, subtree: true });
      }

      sync();
    }

    settleLoadingState() {
      clearTimeout(this.loadingFallback);
      this.addButton = this.root.querySelector('[data-cv-s4-6cam-add]');
      if (!this.addButton) return;

      this.addButton.removeAttribute('data-added');
      this.addButton.classList.remove('loading');
      this.addButton.setAttribute('aria-busy', 'false');
      this.addButton.disabled = !this.currentVariant?.available;
    }

    selectedOptions() {
      return this.optionGroups.map((group) => group.querySelector('input:checked')?.value || '');
    }

    findByOptions() {
      const options = this.selectedOptions();
      return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
    }

    findById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    restoreFromUrl(announce) {
      const id = new URL(window.location.href).searchParams.get('variant') || this.variantInput.value;
      const variant = this.findById(id) || this.findByOptions() || this.variants[0];
      this.checkOptions(variant);
      this.update(variant, { historyMode: 'replace', announce });
    }

    checkOptions(variant) {
      if (!variant) return;
      this.optionGroups.forEach((group, index) => {
        const input = [...group.querySelectorAll('input')].find((node) => node.value === variant.options[index]);
        if (input) input.checked = true;
      });
    }

    update(variant, { historyMode = 'replace', announce = false } = {}) {
      if (!variant) return;
      this.currentVariant = variant;

      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      if (this.paymentTerms) this.paymentTerms.setAttribute('variant-id', variant.id);

      this.optionGroups.forEach((group) => {
        const output = group.querySelector('[data-cv-s4-6cam-option-value]');
        const checked = group.querySelector('input:checked');
        if (output && checked) output.textContent = checked.value;
      });

      this.setText('[data-cv-s4-6cam-price]', variant.priceFormatted);
      this.setText('[data-cv-s4-6cam-sku]', variant.sku || '—');

      const compare = this.root.querySelector('[data-cv-s4-6cam-compare]');
      if (compare) {
        compare.hidden = !(variant.compareAt > variant.price);
        compare.textContent = variant.compareAt > variant.price ? variant.compareAtFormatted : '';
      }

      const addButton = this.root.querySelector('[data-cv-s4-6cam-add]');
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        const label = addButton.querySelector('[data-cv-s4-6cam-add-label]');
        if (label) label.textContent = variant.available ? addButton.dataset.readyLabel : addButton.dataset.unavailableLabel;
      }

      this.updateQuantityRules(variant);
      this.updateHero(variant);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      const state = { variantId: variant.id };
      if (historyMode === 'push') window.history.pushState(state, '', url);
      else window.history.replaceState(state, '', url);

      if (announce && this.liveRegion) {
        this.liveRegion.textContent = `${variant.title}. ${variant.priceFormatted}. ${variant.available ? 'Available' : 'Unavailable'}.`;
      }

      window.dispatchEvent(new CustomEvent('cv-s4-6cam:variant-change', {
        detail: { productId: this.root.dataset.productId, variant },
      }));
    }

    updateQuantityRules(variant) {
      if (!this.quantityInput) return;
      const minimum = Number(variant.quantityMin) || 1;
      const increment = Number(variant.quantityIncrement) || 1;
      this.quantityInput.min = String(minimum);
      this.quantityInput.step = String(increment);
      if (variant.quantityMax == null) this.quantityInput.removeAttribute('max');
      else this.quantityInput.max = String(variant.quantityMax);
      this.normalizeQuantity();
    }

    normalizeQuantity() {
      if (!this.quantityInput) return;
      const minimum = Number(this.quantityInput.min) || 1;
      const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
      const increment = Number(this.quantityInput.step) || 1;
      const raw = Number.parseInt(this.quantityInput.value, 10);
      const safe = Number.isFinite(raw) ? raw : minimum;
      const stepped = minimum + Math.round((safe - minimum) / increment) * increment;
      this.quantityInput.value = String(Math.min(maximum, Math.max(minimum, stepped)));
      const minus = this.root.querySelector('[data-cv-s4-6cam-quantity-minus]');
      const plus = this.root.querySelector('[data-cv-s4-6cam-quantity-plus]');
      if (minus) minus.disabled = Number(this.quantityInput.value) <= minimum;
      if (plus) plus.disabled = Number(this.quantityInput.value) >= maximum;
    }

    stepQuantity(direction) {
      if (!this.quantityInput) return;
      const increment = Number(this.quantityInput.step) || 1;
      const current = Number.parseInt(this.quantityInput.value, 10) || Number(this.quantityInput.min) || 1;
      this.quantityInput.value = String(current + direction * increment);
      this.normalizeQuantity();
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateHero(variant) {
      if (!this.heroImage || !variant.image) return;
      this.heroImage.src = variant.image;
      if (variant.srcset) this.heroImage.srcset = variant.srcset;
      else this.heroImage.removeAttribute('srcset');
      this.heroImage.alt = variant.alt || '';
      if (this.mobileSource && !this.mobileSource.srcset) this.mobileSource.removeAttribute('srcset');
    }

    setText(selector, value) {
      const node = this.root.querySelector(selector);
      if (node) node.textContent = value ?? '';
    }

    openGallery(trigger) {
      if (!this.dialog || !this.gallery.length) return;
      this.returnFocus = trigger;
      this.showGallery(0);
      this.dialog.showModal();
      document.documentElement.classList.add('cv-s4-6cam-dialog-open');
      this.dialog.querySelector('[data-cv-s4-6cam-gallery-close]')?.focus();
    }

    showGallery(index) {
      if (!this.gallery.length || !this.dialogImage) return;
      this.galleryIndex = (index + this.gallery.length) % this.gallery.length;
      const item = this.gallery[this.galleryIndex];
      this.dialogImage.src = item.src;
      this.dialogImage.alt = item.alt || '';
      if (this.dialogCaption) {
        this.dialogCaption.textContent = item.caption || '';
        this.dialogCaption.hidden = !item.caption;
      }
      const position = this.dialog.querySelector('[data-cv-s4-6cam-gallery-position]');
      if (position) position.textContent = `${this.galleryIndex + 1} / ${this.gallery.length}`;
    }

    trapFocus(event) {
      const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((node) => !node.hidden && node.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    closeGallery() {
      if (this.dialog?.open) this.dialog.close();
    }

    destroy() {
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('cv-s4-6cam-dialog-open');
      this.returnFocus = null;
      this.abort.abort();
      this.loadingObserver?.disconnect();
      this.errorObserver?.disconnect();
      this.veroObserver?.disconnect();
      this.veroDocumentObserver?.disconnect();
      cancelAnimationFrame(this.veroFrame);
      this.veroChat?.style.removeProperty('--cv-s4-6cam-vero-bottom');
      this.veroChat?.style.removeProperty('--cv-s4-6cam-vero-right');
      this.veroChat?.removeAttribute('data-cv-s4-6cam-docked');
      clearTimeout(this.loadingFallback);
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.(selector) ? [scope] : [...scope.querySelectorAll(selector)];
    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new CVS46CAMPurchase(root));
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(selector) ? event.target : event.target.querySelector?.(selector);
    if (!root) return;
    controllers.get(root)?.destroy();
    controllers.delete(root);
  });
})();
